import ClubEmployee from '../dto/ClubEmployee';
import ClubEmployeeDepartmentRelation from '../dto/ClubEmployeeDepartmentRelation';
import ClubEmployeeKnowledge from '../dto/ClubEmployeeKnowledge';
import ClubEmployeeRole from '../dto/ClubEmployeeRole';
import ClubEmployeeTeamRelation from '../dto/ClubEmployeeTeamRelation';
import Error from '../model/Error';
import SpokenLanguage from '../dto/SpokenLanguage';
import axios from 'axios';

export default class ClubEmployeeRepository {

    static findAllByClub(clubId) {
        return new Promise((resolve, reject) => {
            axios
                .get('/club/clubs/' + clubId + '/employees')
                .then(response => resolve(response.data.data.map(data => ClubEmployee.fromData(data))))
                .catch(error => reject(Error.fromResponse(error.response)));
        });
    }

    static removeEmployeeDepartmentRelation(employeeId, id) {
        return new Promise((resolve, reject) => {
            axios
                .delete(`/club/employees/${employeeId}/departments/${id}`)
                .then(() => resolve())
                .catch(error => reject(Error.fromResponse(error.response)));
        });
    }

    static removeEmployeeTeamRelation(employeeId, id) {
        return new Promise((resolve, reject) => {
            axios
                .delete(`/club/employees/${employeeId}/teams/${id}`)
                .then(() => resolve())
                .catch(error => reject(Error.fromResponse(error.response)));
        });
    }

    static createDepartmentRelation(departmentId, departmentRelation, employeeId) {
        const data = {
            start: departmentRelation.start,
            end: departmentRelation.end,
            departmentId: departmentId,
        };

        return new Promise((resolve, reject) => {
            axios
                .post(`/club/employees/${employeeId}/departments`, data)
                .then(response => resolve(response.headers['location'].split('/').pop()))
                .catch(error => reject(Error.fromResponse(error.response)));
        });
    }

    static updateDepartmentRelation(departmentRelation) {
        const data = {
            start: departmentRelation.start,
            end: departmentRelation.end,
            departmentId: departmentRelation.department.id,
        };

        return new Promise((resolve, reject) => {
            axios
                .put(`/club/employees/${departmentRelation.employee.id}/departments/${departmentRelation.id}`, data)
                .then(() => resolve())
                .catch(error => reject(Error.fromResponse(error.response)));
        });
    }

    static createTeamRelation(teamId, teamRelation, employeeId) {
        const data = {
            start: teamRelation.start,
            end: teamRelation.end,
            teamId: teamId,
        };

        return new Promise((resolve, reject) => {
            axios
                .post(`/club/employees/${employeeId}/teams`, data)
                .then(response => resolve(response.headers['location'].split('/').pop()))
                .catch(error => reject(Error.fromResponse(error.response)));
        });
    }

    static updateTeamRelation(teamRelation) {
        const data = {
            start: teamRelation.start,
            end: teamRelation.end,
            teamId: teamRelation.team.id,
        };

        return new Promise((resolve, reject) => {
            axios
                .put(`/club/employees/${teamRelation.employeeId}/teams/${teamRelation.id}`, data)
                .then(() => resolve())
                .catch(error => reject(Error.fromResponse(error.response)));
        });
    }

    static findDepartments(employeeId) {
        return new Promise((resolve, reject) => {
            axios
                .get(`/club/employees/${employeeId}/departments`)
                .then(response => resolve(response.data.data.map(data => ClubEmployeeDepartmentRelation.fromData(data))))
                .catch(error => reject(Error.fromResponse(error.response)));
        });
    }

    static findTeams(employeeId) {
        return new Promise((resolve, reject) => {
            axios
                .get(`/club/employees/${employeeId}/teams`)
                .then(response => resolve(response.data.data.map(data => ClubEmployeeTeamRelation.fromData(data))))
                .catch(error => reject(Error.fromResponse(error.response)));
        });
    }

    static removeDepartment(employeeId, departmentId) {
        return new Promise((resolve, reject) => {
            axios
                .delete(`/club/employees/${employeeId}/departments/${departmentId}`)
                .then(() => resolve())
                .catch(error => reject(Error.fromResponse(error.response)));
        });
    }

    static removeTeam(employeeId, teamId) {
        return new Promise((resolve, reject) => {
            axios
                .delete(`/club/employees/${employeeId}/teams/${teamId}`)
                .then(() => resolve())
                .catch(error => reject(Error.fromResponse(error.response)));
        });
    }

    static getLoginData(employeeId) {
        return new Promise((resolve, reject) => {
            axios
                .get(`/club/employees/${employeeId}/login`)
                .then(response => resolve(ClubEmployee.fromLoginData(response.data.data)))
                .catch(error => reject(Error.fromResponse(error.response)));
        });
    }

    static updateLoginData(employee) {
        const data = {clubEmployeeLogin: {contactEmail: employee.contactEmail, enabled: employee.enabled}};
        return new Promise((resolve, reject) => {
            axios
                .put(`/club/employees/${employee.id}/login`, data)
                .then(() => resolve())
                .catch(error => reject(Error.fromResponse(error.response)));
        });
    }

    static getKnowledgeData(employeeId) {
        return new Promise((resolve, reject) => {
            axios
                .get(`/club/employees/${employeeId}/knowledge`)
                .then(response => resolve(ClubEmployeeKnowledge.fromData(response.data.data)))
                .catch(error => reject(Error.fromResponse(error.response)));
        });
    }

    static updateKnowledgeData(employeeId, employeeKnowledge) {

        return new Promise((resolve, reject) => {
            axios
                .put(`/club/employees/${employeeId}/knowledge`, employeeKnowledge.serialize())
                .then(() => resolve())
                .catch(error => reject(Error.fromResponse(error.response)));
        });
    }

    static findSpokenLanguages(employeeId)
    {
        return new Promise((resolve, reject) => {
            axios
                .get(`/club/employees/${employeeId}/spoken-languages`)
                .then(response => resolve(response.data.data.map(data => SpokenLanguage.fromData(data))))
                .catch(error => reject(Error.fromResponse(error.response)));
        });
    }

    static removeSpokenLanguage(employeeId, id) {
        return new Promise((resolve, reject) => {
            axios
                .delete(`/club/employees/${employeeId}/spoken-languages/${id}`)
                .then(() => resolve())
                .catch(error => reject(Error.fromResponse(error.response)));
        });
    }

    static updateSpokenLanguage(employeeId, spokenLanguage) {
        const data = {
            id: spokenLanguage.id,
            languageCode: spokenLanguage.language.code,
            level: spokenLanguage.level,
            invalid: spokenLanguage.invalid,
        };

        return new Promise((resolve, reject) => {
            axios
                .put(`/club/employees/${employeeId}/spoken-languages/${spokenLanguage.id}`, data)
                .then(() => resolve())
                .catch(error => reject(Error.fromResponse(error.response)));
        });
    }

    static createSpokenLanguage(employeeId, spokenLanguage) {
        const data = {
            languageCode: spokenLanguage.language.code,
            level: spokenLanguage.level,
            invalid: spokenLanguage.invalid,
        };

        return new Promise((resolve, reject) => {
            axios
                .post(`/club/employees/${employeeId}/spoken-languages`, data)
                .then(() => resolve())
                .catch(error => reject(Error.fromResponse(error.response)));
        });
    }

    static getClubEmployeeRolesData(employeeId) {
        return new Promise((resolve, reject) => {
            axios
                .get(`/club/employees/${employeeId}/attributes/roles`)
                .then(response => resolve(response.data.data.map(data => ClubEmployeeRole.fromData(data))))
                .catch(error => reject(Error.fromResponse(error.response)));
        });
    }

    static createClubEmployeeRole(employeeId, clubEmployeeRole) {
        return new Promise((resolve, reject) => {
            axios
                .post(`/club/employees/${employeeId}/attributes/roles`, clubEmployeeRole.serialize())
                .then(response => resolve(response.headers['X-Identifier']))
                .catch(error => reject(Error.fromResponse(error.response)));
        });
    }

    static updateClubEmployeeRoles(employeeId, clubEmployeeRoles) {
        return Promise.all(clubEmployeeRoles.map(clubEmployeeRole =>
            new Promise((resolve, reject) => {
                axios
                    .put(`/club/employees/${employeeId}/attributes/roles/${clubEmployeeRole.id}`, clubEmployeeRole.serialize())
                    .then(() => resolve())
                    .catch(error => reject(Error.fromResponse(error.response)));
            })
        ));
    }

    static removeClubEmployeeRole(employeeId, roleId) {
        return new Promise((resolve, reject) => {
            axios
                .delete(`/club/employees/${employeeId}/attributes/roles/${roleId}`)
                .then(() => resolve())
                .catch(error => reject(Error.fromResponse(error.response)));
        });
    }
}
